import { useVirtualizer } from '@tanstack/react-virtual';
import { type ReactNode, useCallback, useRef } from 'react';
import { cn } from '@/app/utils';
import {
  FILE_ROW_HEIGHT,
  FOLDER_ROW_HEIGHT,
  GRID_GAP_PX,
  SECTION_ROW_HEIGHT,
  VIRTUAL_LIST_OVERSCAN,
} from '../../../constants';
import type { ClawFileItem, MediaRow } from '../../../types';

interface MediaBrowserVirtualListProps {
  rows: MediaRow[];
  columnCount: number;
  renderFolder: (item: ClawFileItem) => ReactNode;
  renderFile: (item: ClawFileItem) => ReactNode;
}

export const MediaBrowserVirtualList = ({
  rows,
  columnCount,
  renderFolder,
  renderFile,
}: MediaBrowserVirtualListProps) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    getItemKey: (index) => rows[index]?.key ?? index,
    estimateSize: (index) => {
      const row = rows[index];
      if (!row) return FILE_ROW_HEIGHT;
      if (row.type === 'section') return SECTION_ROW_HEIGHT;
      if (row.type === 'folders') return FOLDER_ROW_HEIGHT;
      return FILE_ROW_HEIGHT;
    },
    overscan: VIRTUAL_LIST_OVERSCAN,
  });

  const measureRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      rowVirtualizer.measureElement(node);
    },
    [rowVirtualizer],
  );

  return (
    <div ref={scrollContainerRef} className="h-full overflow-y-auto p-4">
      <div
        className="relative w-full"
        style={{
          height: rowVirtualizer.getTotalSize(),
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const row = rows[virtualRow.index];
          if (!row) return null;

          const isSection = row.type === 'section';
          const renderItem = row.type === 'folders' ? renderFolder : renderFile;

          return (
            <div
              key={row.key}
              ref={measureRef}
              data-index={virtualRow.index}
              className={cn('absolute top-0 left-0 w-full', isSection ? 'pb-2' : 'pb-4')}
              style={{ transform: `translateY(${virtualRow.start}px)` }}
            >
              {isSection ? (
                <div className="text-system-white40 text-xs uppercase tracking-[0.12em]">{row.title}</div>
              ) : (
                <div
                  className="grid"
                  style={{
                    gap: `${GRID_GAP_PX}px`,
                    gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                  }}
                >
                  {row.items.map((item) => (
                    <div key={item.path}>{renderItem(item)}</div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
